import moment from 'moment';

import { swarmRequest } from "@common/helpers/util/requestMiddleware";
import CasinoMiddleware from "@tickets/services/casinoMiddleware";
import { chain } from "@common/helpers/declarative";
import { getAuthData } from "@common/helpers/auth";
import Config from "@app/dev/config/main";

import { t } from '@common/helpers/translator';
import { isCasinoTicket, isDepositTicket, isSportTicket } from "./type";
import { normalizeCasinoBarcode, normalizeDepositBarcode, normalizeSportBarcode } from "./barcode";
import { getTicketError } from "./error";
import { getScoresForSelections } from "./scores";

export const tryProcessDepositTicket = async (next, barcode) => {
  if (!isDepositTicket(barcode)) return next();

  const params = {
    doc_id: normalizeDepositBarcode(barcode),
    token: getAuthData()?.token,
  };

  const result = await swarmRequest(params, "check_terminal_tito_document");

  return {
    dataFromSwarm: { ...result?.data },
    ticketError: getTicketError(result),
  };
};

export const tryProcessCasinoTicket = async (next, barcode) => {
  if (!isCasinoTicket(barcode)) return next();

  const externalId = normalizeCasinoBarcode(barcode);

  const params = {
    ExternalId: externalId,
    token: getAuthData()?.token,
  };

  try {
    const [swarmResult, casinoResult] = await Promise.all([
      swarmRequest(params, "terminal_get_casino_bets"),
      CasinoMiddleware.getTickets([externalId]),
    ]);

    const dataFromCasino = casinoResult?.[0];

    const createdDate = dataFromCasino?.BetMultiplyInfo[0]?.CreateDate;
    const isTicketExpired = createdDate && moment().diff(moment(createdDate), "days") > Config.main.ticketScanExpirationDays;

    if (isTicketExpired) {
      throw new Error("Ticket is expired");
    }

    const swarmError = getTicketError(swarmResult);

    if (!swarmResult?.Data?.length) {
      if (!casinoResult) throw new Error("Bet not found");
      else throw new Error("Please try again later");
    }

    return {
      dataFromSwarm: { ...swarmResult?.Data?.[0] },
      dataFromCasino,
      ticketError: swarmError?.error ? swarmError : !swarmResult && getTicketError(casinoResult),
    };
  } catch (e) {
    return {
      dataFromSwarm: {},
      ticketError: {
        error: true,
        errorText: e?.message || t("Wrong barcode"),
      },
    };
  }
};

export const tryProcessSportTicket = async (next, barcode) => {
  if (!isSportTicket(barcode)) return next();

  const params = {
    bar_code: normalizeSportBarcode(barcode),
    is_with_cashout: true,
    token: getAuthData()?.token,
  };

  const result = await swarmRequest(params, "check_terminal_bet_ticket");

  if (result?.data?.selection_list) {
    const scores = await getScoresForSelections(result.data.selection_list);

    result.data.selection_list = result.data.selection_list.map((selection) => ({
      ...selection,
      match_info: scores[selection.match_id],
    }));
  }

  return {
    dataFromSwarm: { ...result?.data },
    ticketError: getTicketError(result),
    ticketType: "sport",
  };
};

export const processBetTicketBarcode = chain(
  tryProcessDepositTicket,
  tryProcessCasinoTicket,
  tryProcessSportTicket,
  () => {
    throw new Error(t("Wrong barcode"));
  },
);
